import React, { useState, useMemo, useCallback, useEffect } from 'react'

import { Button, Icon, FileEmpty } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, string } from 'prop-types'

import Pagination from 'Pods/Shared/Components/Pagination/Pagination'
import I18n from 'Pods/Shared/I18n'

import ColumnVisibilityMenu from './ColumnVisibilityMenu'
import RegularEarningsDetailTable from './RegularEarningsDetailTable'
import RegularEarningsEmptyState from './RegularEarningsEmptyState'
import { makeFinalPayoutCell } from './renderers'
import useEarningsDetail, { DEFAULT_PER_PAGE } from '../../../Hooks/useEarningsDetail'
import { useRefundsModal } from '../../../Hooks/useRefundsModal'
import {
  EARNINGS_DETAIL_TYPES,
  PER_PAGE_OPTIONS,
  REGULAR_EARNINGS_PANEL_CONFIG,
} from '../../../Static/earningsDetailConfig'
import { formatMonthLabel } from '../../../Static/monthOptions'
import {
  regularSalesColumns,
  regularSalesMenuColumns,
} from '../../../Static/regularEarningsColumns'
import RefundsModal from '../PaymentsInfo/RefundsModal'

const INITIAL_VISIBLE_KEYS = []
const TOGGLEABLE_KEYS = new Set(regularSalesMenuColumns.map((col) => col.key))
const { sales: salesEmptyCopy, salesForMonth: salesForMonthEmptyCopy } =
  REGULAR_EARNINGS_PANEL_CONFIG.emptyState

function RegularEarningsSalesTab({ isActive = false, selectedMonth = null }) {
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)
  const [visibleKeys, setVisibleKeys] = useState(INITIAL_VISIBLE_KEYS)

  useEffect(() => { setPage(1) }, [selectedMonth])

  const {
    rows, meta, isLoading, generateReport, isReportRequired,
  } = useEarningsDetail({
    type: EARNINGS_DETAIL_TYPES.sales, page, perPage, month: selectedMonth, enabled: isActive,
  })

  const { refundsState, openRefundsModal, closeRefundsModal } = useRefundsModal()

  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', ...options }),
    []
  )

  const effectiveColumns = useMemo(() => {
    const visibleSet = new Set(visibleKeys)
    const finalPayoutCell = makeFinalPayoutCell(openRefundsModal, translate)
    return regularSalesColumns.map((col) => {
      const base = col.key === 'finalPayout' ? { ...col, cell: finalPayoutCell } : col
      if (!TOGGLEABLE_KEYS.has(base.key)) return base
      return { ...base, visible: visibleSet.has(base.key) }
    })
  }, [visibleKeys, openRefundsModal, translate])

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage)
  }, [])

  const handlePerPageChange = useCallback((nextPerPage) => {
    setPerPage(nextPerPage)
    setPage(1)
  }, [])

  const hasRows = rows.length > 0
  const showEmptyState = !isLoading && !hasRows
  const showFooter = hasRows && meta.totalCount > 0

  return (
    <div className="regular-earnings-detail-panel__tab">
      <div className="regular-earnings-detail-panel__toolbar">
        <ColumnVisibilityMenu
          columns={regularSalesMenuColumns}
          visibleKeys={visibleKeys}
          onChange={setVisibleKeys}
        />
      </div>

      {showEmptyState ? (
        <RegularEarningsEmptyState
          title={selectedMonth ? salesForMonthEmptyCopy.title : salesEmptyCopy.title}
          description={selectedMonth
            ? salesForMonthEmptyCopy.description(formatMonthLabel(selectedMonth))
            : salesEmptyCopy.description}
        />
      ) : (
        <RegularEarningsDetailTable
          rows={rows}
          columns={effectiveColumns}
          isLoading={isLoading}
        />
      )}

      {showFooter && (
        <>
          <div className="regular-earnings-detail-panel__report">
            {isReportRequired ? (
              <p className="cgt-body-base">{translate('profile.my_sales.earnings_detail.report_sent_confirmation')}</p>
            ) : (
              <Button
                type="secondary"
                size="small"
                icon={<Icon svgComponent={FileEmpty} />}
                onClick={generateReport}
              >
                {translate('profile.my_sales.earnings_detail.generate_report')}
              </Button>
            )}
          </div>
          <div className="regular-earnings-detail-panel__pagination">
            <Pagination
              currentPage={meta.page}
              totalPages={Math.max(meta.totalPages, 1)}
              totalCount={meta.totalCount}
              perPage={meta.perPage}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
              perPageOptions={PER_PAGE_OPTIONS}
              alwaysShow
            />
          </div>
        </>
      )}
      <RefundsModal
        isOpen={refundsState.isOpen}
        onClose={closeRefundsModal}
        refundsData={refundsState.refundsData}
        isLoading={refundsState.isLoading}
      />
    </div>
  )
}

RegularEarningsSalesTab.propTypes = {
  isActive: bool,
  selectedMonth: string,
}

export default RegularEarningsSalesTab
